import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import LoadingButton from '@mui/lab/LoadingButton';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Snackbar from '@mui/material/Snackbar';
import TextField from '@mui/material/TextField';
import { ApiCheckDeleteStep, ApiCheckDeleteStepResult } from '@shared/interfaces/ApiInterfaces';
import * as React from 'react';
import { useEffect, useState } from 'react';
import BackendApiService from '../services/BackendApiService';
import { getAccessToken } from '../utils/accessTokenStore';
import { MultiError } from '../utils/errors';

type StepState = "pending" | "running" | "success" | "failed";

const CHECK_STEPS: { key: ApiCheckDeleteStep; label: string; successLabel: string; failedLabel: string }[] = [
  {
    key: "status",
    label: "Check request status",
    successLabel: "Request completed and provisioned",
    failedLabel: "Request not completed or still in progress",
  },
  {
    key: "delete-requests",
    label: "Check existing delete requests",
    successLabel: "No open delete request found",
    failedLabel: "A delete request already exists",
  },
  {
    key: "dependencies",
    label: "Check resource dependencies",
    successLabel: "No dependencies found - ready for delete",
    failedLabel: "Dependent resources found",
  },
];

const initialStepStates = () =>
  Object.fromEntries(CHECK_STEPS.map(s => [s.key, "pending"])) as Record<ApiCheckDeleteStep, StepState>;

function StepIcon({ state }: { state: StepState }) {
  switch (state) {
    case "running":
      return <CircularProgress size={20} />;
    case "success":
      return <CheckCircleIcon color="success" />;
    case "failed":
      return <ErrorIcon color="error" />;
    default:
      return <RadioButtonUncheckedIcon color="disabled" />;
  }
}

type DeleteResourceProps = {
  open: boolean;
  onClose: () => void;
  requestId: string;
  disabled?: boolean;
  mode?: "delete" | "invalidate";
  module: {
    platform: string;
    name: string;
    id: string;
    resourceName: string;
  },
  onSuccess?: () => void;
}

export default function DeleteResourceDialog(props: DeleteResourceProps) {
  const [stepStates, setStepStates] = useState(initialStepStates);
  const [failedResult, setFailedResult] = useState<ApiCheckDeleteStepResult>();
  const [checkError, setCheckError] = useState<string>();
  const [checksPassed, setChecksPassed] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const [snackOpen, setSnackOpen] = useState(false);
  const [resultErrorMessage, setResultErrorMessage] = useState<string>("");
  const [invalidationNote, setInvalidationNote] = useState<string>("");

  const [accessToken] = useState(() => {
    return getAccessToken();
  });

  const mode = props.mode || "delete";
  const isInvalidateMode = mode === "invalidate";
  const { platform, name, id: moduleId } = props.module;

  useEffect(() => {
    if (!props.open) {
      return;
    }
    let cancelled = false;
    setInvalidationNote("");
    setFailedResult(undefined);
    setCheckError(undefined);
    setChecksPassed(false);
    setStepStates(initialStepStates());

    (async () => {
      const backendApiService = new BackendApiService(accessToken ?? '');
      for (const { key } of CHECK_STEPS) {
        setStepStates(prev => ({ ...prev, [key]: "running" }));
        let result: ApiCheckDeleteStepResult;
        try {
          result = await backendApiService.checkDeleteStep(platform, name, props.requestId, moduleId, key);
        } catch (err) {
          if (cancelled) {
            return;
          }
          setStepStates(prev => ({ ...prev, [key]: "failed" }));
          setCheckError(err instanceof MultiError ? err.errors.join(", ") : err instanceof Error ? err.message : `${err}`);
          return;
        }
        if (cancelled) {
          return;
        }
        setStepStates(prev => ({ ...prev, [key]: result.passed ? "success" : "failed" }));
        if (!result.passed) {
          setFailedResult(result);
          return;
        }
      }
      setChecksPassed(true);
    })();

    return () => {
      cancelled = true;
    };
  }, [props.open, accessToken, platform, name, moduleId, props.requestId]);

  const handleCancel = () => {
    props.onClose();
  };

  const handleDelete = async () => {
    setResultErrorMessage("");
    setLoading(true);
    try {
      const backendApiService = new BackendApiService(accessToken ?? '');
      const deleteResponse = await backendApiService.deleteModuleRequest(
        props.module.platform,
        props.module.name,
        props.requestId,
        props.module.id,
        isInvalidateMode ? {
          markAsInvalid: true,
          invalidationReason: invalidationNote.trim() || "Marked by IaC.Admin as invalid create request"
        } : undefined
      );

      console.log("deleteResponse", deleteResponse);
      if (props.onSuccess) {
        props.onSuccess();
      }
    } catch (err) {
      console.log("delete error", err);
      setResultErrorMessage(`${err}`);
    }

    setLoading(false);
    props.onClose();
    setSnackOpen(true);
  };

  const handleSnackClose = () => {
    setSnackOpen(false);
  };

  return (
    <React.Fragment>
      <Dialog
        open={props.open}
        onClose={handleCancel}
        fullWidth={true}
        maxWidth="sm"
      >
        <DialogTitle>
          {isInvalidateMode ? "Reset Request (creates delete request)" : "Delete"} {props.module.resourceName}
        </DialogTitle>
        <DialogContent>
          <List dense disablePadding>
            {CHECK_STEPS.map(({ key, label, successLabel, failedLabel }) => (
              <ListItem key={key} disableGutters>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <StepIcon state={stepStates[key]} />
                </ListItemIcon>
                <ListItemText primary={
                  stepStates[key] === "success" ? successLabel
                    : stepStates[key] === "failed" && !checkError ? failedLabel
                      : label
                } />
              </ListItem>
            ))}
          </List>

          {checkError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              The check could not be executed.<br />
              {checkError}
            </Alert>
          )}

          {failedResult && (
            failedResult.dependentModules?.length ?
              <>
                <Alert severity="warning" sx={{ mt: 2 }}>
                  The resource cannot be deleted because of the following dependencies:
                  <ul>
                    {failedResult.dependentModules.map((module, index) =>
                      <li key={index}>
                        <span style={{ textTransform: 'capitalize' }}>{module.platform} {module.name}</span>&nbsp;<span>({module.resourceName})</span>
                      </li>)}
                  </ul>
                </Alert>
                <DialogContentText sx={{ marginTop: 2 }}>
                  Please remove the dependent resources first.
                </DialogContentText>
              </>
              :
              <Alert severity="warning" sx={{ mt: 2 }}>
                The resource cannot be deleted in this status.<br />
                {failedResult.message}
              </Alert>
          )}

          {checksPassed && (
            <>
              <DialogContentText sx={{ mt: 2 }}>
                {isInvalidateMode
                  ? `Do you really want to reset request ${props.requestId} and create a delete request for ${props.module.resourceName}?`
                  : `Do you really want to delete resource ${props.module.resourceName}?`}
              </DialogContentText>
              {isInvalidateMode && (
                <TextField
                  sx={{ mt: 2 }}
                  label="Reason for reset (optional)"
                  value={invalidationNote}
                  onChange={(e) => setInvalidationNote(e.target.value)}
                  fullWidth
                  multiline
                  minRows={3}
                  inputProps={{ maxLength: 500 }}
                  helperText={`${invalidationNote.length}/500`}
                />
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel} autoFocus variant='outlined'>Cancel</Button>
          <LoadingButton
            onClick={handleDelete}
            disabled={!checksPassed}
            color="error"
            loading={loading}
            variant="outlined"
          >
            {isInvalidateMode ? "Reset Request" : "Delete"}
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackOpen} autoHideDuration={8000} onClose={handleSnackClose}>
        <Alert
          onClose={handleSnackClose}
          severity={resultErrorMessage ? "error" : "success"}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {resultErrorMessage ?
            <>
              Error creating {isInvalidateMode ? "reset" : "delete"} request for resource {props.module.resourceName}:
              <br />
              {resultErrorMessage}
            </>
            :
            <>
              {isInvalidateMode ? "Reset" : "Delete"} request for resource {props.module.resourceName} was successfully created
            </>
          }
        </Alert>
      </Snackbar>
    </React.Fragment >
  );
}
